import React, { Component, FocusEventHandler, KeyboardEvent } from 'react';
import {
  OutlinedInputProps,
  SlotProps,
  SxProps,
  TextField,
  TextFieldInputSlotPropsOverrides,
  TextFieldOwnerState,
  Theme,
} from '@mui/material';

import { withKey } from '../utilities';
import { MaterialPropertyValuePair } from './MaterialPropertyValuePair';

interface Props {
  readonly id?: string;
  readonly property: string;
  readonly value: string;
  readonly sx?: SxProps<Theme>;
  readonly size?: 'small' | 'medium';

  /**
   * If required and field is empty on submission, it will revert to the original value.
   */
  readonly required?: boolean;
  readonly submit?: (value: string) => void;

  readonly disabled?: boolean;
  readonly error?: boolean;

  readonly inputRef?: React.Ref<HTMLInputElement>;

  readonly multiline?: boolean;
  readonly blurOnEnter?: boolean;

  readonly fullWidth?: boolean;

  readonly InputProps?: SlotProps<
    React.ElementType<OutlinedInputProps>,
    TextFieldInputSlotPropsOverrides,
    TextFieldOwnerState
  >;

  readonly onFocus?: FocusEventHandler<HTMLInputElement>;
  readonly onBlur?: FocusEventHandler<HTMLInputElement>;
}

interface State {
  value: string;
}

const style = {
  marginBottom: 16,
};

export class PropertyTextInput extends Component<Props, State> {
  readonly state: State = {
    value: this.props.value,
  };

  onChange: React.ReactEventHandler<HTMLInputElement> = (event) => {
    const { value } = event.currentTarget;
    this.setState({ value });
  };

  onFocus: React.FocusEventHandler<HTMLInputElement> = (event) => {
    if (this.props.onFocus) {
      this.props.onFocus(event);
    } else {
      event.target.select();
    }
  };

  onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (this.props.multiline) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();

      if (this.props.blurOnEnter !== false) (event.target as HTMLElement).blur();

      this.onSubmit();
    }
  };

  onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (this.props.onBlur) this.props.onBlur(event);
    if (event.isPropagationStopped()) return;

    this.onSubmit();
  };

  onSubmit = () => {
    if (typeof this.props.submit === 'function') {
      if (this.isValid()) {
        this.props.submit(this.state.value);
      }
      this.setState({ value: this.props.value });
    }
  };

  isValid = () => (this.props.required ? this.state.value !== '' : true);

  render() {
    if (this.props.disabled) {
      return <MaterialPropertyValuePair sx={this.props.sx} property={this.props.property} value={this.props.value} />;
    }

    return (
      <TextField
        size={this.props.size ?? 'small'}
        sx={this.props.sx}
        margin="normal"
        variant="outlined"
        id={this.props.id}
        inputRef={this.props.inputRef}
        style={style}
        label={this.props.property}
        value={this.state.value}
        onFocus={this.onFocus}
        onChange={this.onChange as any}
        onKeyDown={this.onKeyDown}
        error={!this.isValid() || this.props.error}
        onBlur={this.onBlur}
        multiline={this.props.multiline}
        fullWidth={this.props.fullWidth}
        slotProps={{ input: this.props.InputProps }}
      />
    );
  }
}

export default withKey('value', PropertyTextInput);
